import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ILocaleNameLookup, LocaleAbbreviation } from './ILocaleNameLookup';

/** The default list of locales for the selection menu. */
const abbreviations: ILocaleNameLookup = {
    'en-US': 'English',
    'es-US': 'Español',
    af: 'Afrikaans',
    sq: 'shqiptar',
    ar: 'عربى',
    az: 'Azərbaycan',
    eu: 'Euskal',
    bn: 'বাঙালি',
    be: 'беларускі',
    bg: 'български',
    ca: 'Català',
    'zh-CN': '簡化字',
    'zh-TW': '中文',
    hr: 'Hrvatski',
    cs: 'čeština',
    da: 'dansk',
    nl: 'Nederlands',
    eo: 'Esperanto',
    et: 'Eesti keel',
    tl: 'Filipino',
    fi: 'Suomalainen',
    'fr-CA': 'français',
    gl: 'Galego',
    ka: 'Georgian',
    de: 'Deutsche',
    el: 'Ελληνικά',
    gu: 'ગુજરાતી',
    ht: 'Kreyòl Ayisyen',
    iw: 'עברית',
    hi: 'हिंदी',
    hu: 'Magyar',
    is: 'Íslensku',
    id: 'bahasa Indonesia',
    ga: 'Gaeilge',
    it: 'italiano',
    ja: '日本人',
    kn: 'ಕನ್ನಡ',
    ko: '한국어',
    la: 'Latine',
    lv: 'Latviešu valoda',
    lt: 'Lietuvių',
    mk: 'Македонски',
    ms: 'Melayu',
    mt: 'Malti',
    no: 'norsk',
    fa: 'فارسی',
    pl: 'Polskie',
    ro: 'Română',
    ru: 'русский',
    sr: 'Српски',
    sk: 'slovenský',
    sl: 'Slovenščina',
    sw: 'Kiswahili',
    sv: 'svenska',
    ta: 'தமிழ்',
    te: 'తెలుగు',
    th: 'ไทย',
    tr: 'Türk',
    uk: 'Українська',
    ur: 'اردو',
    vi: 'Tiếng Việt',
    cy: 'Cymraeg',
    yi: 'ייִדיש'
};

/**
 * A component that displays a button that, when pressed, reveals a menu for selecting the Angular application's locale. Note that in the interface this is
 * presented as "language" selection.
 *
 * This is a quick port of the code from the slider header and may not be a 1 to 1 recreation. Making a selection simply emits the choice and doesn't actually set the
 * application's locale. That responsibility is left to the parent component.
 */
@Component({
    // tslint:disable-next-line:component-selector
    selector: 'ofi-foundation-locale-select',
    templateUrl: './locale-select.component.html',
    styleUrls: ['./locale-select.component.scss'],
    standalone: false
})
export class FoundationLocaleSelectComponent implements OnChanges {
    /** A language to be selected by default */
    @Input() locale: LocaleAbbreviation = 'en-US';
    /** A list of languages that can be selected from. */
    @Input() locales: ILocaleNameLookup = abbreviations;

    /** An event indicating a change to the language selected. */
    @Output() localeSelectionChanged = new EventEmitter<string>();

    /** @ignore */
    ngOnChanges (changes: SimpleChanges): void {
        if (changes && changes.locales) {
            this.locales = changes.locales.currentValue;
        }
        if (changes && changes.locale) {
            this.locale = changes.locale.currentValue;
        }
        if (this.locale === undefined) {
            this.locale = 'en-US';
        }
        if (this.locales === undefined) {
            this.locales = abbreviations;
        }
    }

    /** Flip the key and values? */
    reverseHash (input: any): any {
        const output = {};
        for (const key in input) {
            if (input[key]) {
                output[input[key]] = key;
            }
        }
        return output;
    }

    /** Get the list of locales. */
    get languages (): any {
    // For loop here due to IE not supporting Object.values()
        const languages = [];
        for (const lang in this.locales) {
            if (lang) {
                languages.push(this.locales[lang]);
            }
        }
        return languages;
    }

    /** Retrieve a list with flipped keys and values? */
    get reversedLangAbbreviations (): any {
        return this.reverseHash(this.locales);
    }

    /** Used for menu item click events. */
    chooseLanguage (lang: string): void {
        this.locale = this.reversedLangAbbreviations[lang];
        this.localeSelectionChanged.emit(this.reversedLangAbbreviations[lang]);
    }

    /**
   * A trackBy function for use when looping over languages with ngFor.
   * @arg index: The index of the current item.
   * @arg language: The current language... abbreviation?.
   */
    trackByLanguageFn (index: number, language: string): string {
        return `${language}`;
    }
}
